import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { FileSpreadsheet, FileText, Upload, Download, CheckCircle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ImportApneaTablesDialog — Excel/CSV + PDF (AI) import for apnea tables.
 */
const ImportApneaTablesDialog = ({ open, onClose, onComplete }) => {
  const [importMode, setImportMode] = useState('excel');
  const [excelFile, setExcelFile] = useState(null);
  const [excelResults, setExcelResults] = useState(null);
  const [excelImporting, setExcelImporting] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfImporting, setPdfImporting] = useState(false);
  const [pdfResults, setPdfResults] = useState(null);
  const excelRef = useRef(null);
  const pdfRef = useRef(null);

  const reset = () => {
    setExcelFile(null);
    setExcelResults(null);
    setPdfFile(null);
    setPdfImporting(false);
    setPdfResults(null);
    setImportMode('excel');
    if (excelRef.current) excelRef.current.value = '';
    if (pdfRef.current) pdfRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/apnea-tables/import-excel/template`);
      const { headers, example_rows } = res.data;
      const csv = [
        headers.join(','),
        ...example_rows.map((r) => headers.map((h) => r[h] ?? '').join(',')),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'apnea_tables_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur de téléchargement du template');
    }
  };

  const handleExcelImport = async () => {
    if (!excelFile) return;
    setExcelImporting(true);
    setExcelResults(null);
    try {
      const fd = new FormData();
      fd.append('file', excelFile);
      const res = await axios.post(`${API_BASE_URL}/api/apnea-tables/import-excel`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setExcelResults(res.data);
      if (res.data?.total_created > 0 && onComplete) onComplete();
    } catch (err) {
      setExcelResults({
        total_processed: 0,
        total_created: 0,
        total_errors: 1,
        errors: [{ name: '?', error: err.response?.data?.detail || 'Unknown error' }],
      });
    } finally {
      setExcelImporting(false);
    }
  };

  const handlePdfImport = async () => {
    if (!pdfFile) return;
    setPdfImporting(true);
    setPdfResults(null);
    try {
      const fd = new FormData();
      fd.append('file', pdfFile);
      const res = await axios.post(`${API_BASE_URL}/api/apnea-tables/import-pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      setPdfResults(res.data);
      if (res.data?.total_created > 0 && onComplete) onComplete();
    } catch (err) {
      setPdfResults({
        error: true,
        message: err.response?.data?.detail || err.message || 'Import failed',
      });
    } finally {
      setPdfImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="bg-gradient-to-br from-cyan-900 to-blue-950 border-cyan-700 text-white max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-cyan-200 flex items-center">
            <FileSpreadsheet className="mr-2" size={20} />
            Importer des tables d'apnée
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex rounded-lg overflow-hidden border border-cyan-600/50">
            <button
              onClick={() => setImportMode('excel')}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${importMode === 'excel' ? 'bg-cyan-600 text-white' : 'bg-cyan-900/30 text-cyan-300 hover:bg-cyan-800/50'}`}
              data-testid="apnea-import-mode-excel"
            >
              <FileSpreadsheet size={16} />
              Excel / CSV
            </button>
            <button
              onClick={() => setImportMode('pdf')}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${importMode === 'pdf' ? 'bg-teal-600 text-white' : 'bg-cyan-900/30 text-cyan-300 hover:bg-cyan-800/50'}`}
              data-testid="apnea-import-mode-pdf"
            >
              <FileText size={16} />
              PDF (IA)
            </button>
          </div>

          {importMode === 'excel' ? (
            <>
              <div className="p-4 bg-cyan-800/30 rounded-lg border border-cyan-600/30">
                <p className="text-cyan-300 text-sm mb-3">
                  Téléchargez le template, remplissez vos tables (1 ligne par étape) et uploadez-le ici.
                </p>
                <Button onClick={downloadTemplate} variant="outline" className="border-cyan-500 text-cyan-200 hover:bg-cyan-700/50" data-testid="apnea-download-template">
                  <Download className="mr-2" size={16} />
                  Télécharger le template
                </Button>
              </div>

              <input
                type="file"
                ref={excelRef}
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setExcelFile(e.target.files[0])}
                className="hidden"
              />
              <div
                onClick={() => excelRef.current?.click()}
                className="border-2 border-dashed border-cyan-600 rounded-lg p-6 text-center cursor-pointer hover:border-cyan-500 hover:bg-cyan-800/30 transition-colors"
              >
                {excelFile ? (
                  <div className="flex items-center justify-center gap-2 text-cyan-300">
                    <CheckCircle size={20} />
                    <span>{excelFile.name}</span>
                  </div>
                ) : (
                  <div className="text-cyan-300">
                    <Upload size={32} className="mx-auto mb-2 opacity-50" />
                    <p>Sélectionner un fichier Excel ou CSV</p>
                    <p className="text-xs text-cyan-400 mt-1">.xlsx, .xls, .csv</p>
                  </div>
                )}
              </div>

              {excelResults && (
                <div className="space-y-3 p-4 bg-cyan-800/30 rounded-lg border border-cyan-700/50">
                  <h4 className="font-semibold text-cyan-200">Résultats</h4>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-2 bg-cyan-700/30 rounded">
                      <div className="text-2xl font-bold text-cyan-200">{excelResults.total_processed}</div>
                      <div className="text-xs text-cyan-400">Traités</div>
                    </div>
                    <div className="p-2 bg-teal-700/30 rounded">
                      <div className="text-2xl font-bold text-teal-300">{excelResults.total_created}</div>
                      <div className="text-xs text-teal-400">Créées</div>
                    </div>
                    <div className="p-2 bg-red-700/30 rounded">
                      <div className="text-2xl font-bold text-red-300">{excelResults.total_errors}</div>
                      <div className="text-xs text-red-400">Erreurs</div>
                    </div>
                  </div>
                  {excelResults.errors?.length > 0 && (
                    <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                      {excelResults.errors.map((e, idx) => (
                        <div key={idx} className="text-xs p-2 bg-red-900/30 rounded border border-red-700/50">
                          <span className="text-red-200">{e.name}: {e.error}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="p-4 bg-teal-800/30 rounded-lg border border-teal-600/30">
                <p className="text-teal-300 text-sm">
                  Uploadez un PDF de tables d'apnée. L'IA détectera automatiquement les tables, leur type (CO2/O2/PV) et leur difficulté.
                </p>
              </div>

              <input
                type="file"
                ref={pdfRef}
                accept=".pdf"
                onChange={(e) => setPdfFile(e.target.files[0])}
                className="hidden"
              />
              <div
                onClick={() => pdfRef.current?.click()}
                className="border-2 border-dashed border-teal-600 rounded-lg p-6 text-center cursor-pointer hover:border-teal-500 hover:bg-teal-800/30 transition-colors"
                data-testid="apnea-pdf-upload-zone"
              >
                {pdfFile ? (
                  <div className="flex items-center justify-center gap-2 text-teal-300">
                    <CheckCircle size={20} />
                    <span>{pdfFile.name}</span>
                  </div>
                ) : (
                  <div className="text-teal-300">
                    <FileText size={32} className="mx-auto mb-2 opacity-50" />
                    <p>Sélectionner un PDF</p>
                    <p className="text-xs text-teal-400 mt-1">.pdf</p>
                  </div>
                )}
              </div>

              {pdfImporting && (
                <div className="p-4 bg-teal-800/30 rounded-lg border border-teal-600/30 text-center">
                  <div className="animate-pulse text-teal-300 text-sm">L'IA analyse le document...</div>
                </div>
              )}

              {pdfResults && !pdfResults.error && (
                <div className="space-y-3 p-4 bg-teal-800/30 rounded-lg border border-teal-700/50">
                  <h4 className="font-semibold text-teal-200">Import réussi</h4>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="p-2 bg-teal-700/30 rounded">
                      <div className="text-2xl font-bold text-teal-200">{pdfResults.total_processed}</div>
                      <div className="text-xs text-teal-400">Détectées</div>
                    </div>
                    <div className="p-2 bg-green-700/30 rounded">
                      <div className="text-2xl font-bold text-green-300">{pdfResults.total_created}</div>
                      <div className="text-xs text-green-400">Créées</div>
                    </div>
                    <div className="p-2 bg-red-700/30 rounded">
                      <div className="text-2xl font-bold text-red-300">{pdfResults.total_errors}</div>
                      <div className="text-xs text-red-400">Erreurs</div>
                    </div>
                  </div>
                  {pdfResults.tables?.length > 0 && (
                    <div className="space-y-1 max-h-32 overflow-y-auto text-xs">
                      {pdfResults.tables.map((t, idx) => (
                        <div key={idx} className="p-2 bg-teal-900/30 rounded border border-teal-700/40">
                          <span className="text-teal-200 font-medium">{t.name}</span>
                          <span className="text-teal-400 ml-2">[{t.type}{t.difficulty ? ` / ${t.difficulty}` : ''}] - {t.steps} étapes</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {pdfResults?.error && (
                <div className="p-4 bg-red-900/30 rounded-lg border border-red-700/50">
                  <p className="text-red-300 text-sm">{pdfResults.message}</p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClose} className="border-cyan-500 text-cyan-300 hover:bg-cyan-700/50">
            Fermer
          </Button>
          {importMode === 'excel' && !excelResults && (
            <Button
              onClick={handleExcelImport}
              disabled={!excelFile || excelImporting}
              className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700"
              data-testid="apnea-excel-submit"
            >
              {excelImporting ? <span className="animate-spin mr-2">...</span> : <Upload className="mr-2" size={16} />}
              Importer
            </Button>
          )}
          {importMode === 'pdf' && !pdfResults && (
            <Button
              onClick={handlePdfImport}
              disabled={!pdfFile || pdfImporting}
              className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700"
              data-testid="apnea-pdf-submit"
            >
              {pdfImporting ? <span className="animate-pulse mr-2">...</span> : <FileText className="mr-2" size={16} />}
              Analyser le PDF
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportApneaTablesDialog;
